// config/plans.ts
// Frontend plan presets configuration
// Single source of truth for plan limits in the UI

export type BasePlanKey = "trial" | "basic" | "vip" | "platinum" | "enterprise";

export type PlanKey =
  | BasePlanKey
  | "trial_plus"
  | "basic_plus"
  | "vip_plus"
  | "platinum_plus"
  | "enterprise_plus";

export interface PlanLimits {
  smsPerMonth: number | "unlimited";
  whatsappPerMonth: number | "unlimited";
  aiRequestsPerMonth: number | "unlimited";
  automations: number | "unlimited";
  apiCallsPerMonth: number | "unlimited";
  storageMb: number | "unlimited"; // always stored as MB when numeric
}

/**
 * PLAN_PRESETS - Default limits for each base plan
 * These values are used to auto-fill form fields when a plan is selected
 * and to detect when limits exceed the plan (triggering Plus variant)
 */
export const PLAN_PRESETS: Record<BasePlanKey, PlanLimits> = {
  trial: {
    smsPerMonth: 0,
    whatsappPerMonth: 0,
    aiRequestsPerMonth: 0,
    automations: 0,
    apiCallsPerMonth: 2000,
    storageMb: 500,     // 500 MB
  },
  basic: {
    smsPerMonth: 500,
    whatsappPerMonth: 500,
    aiRequestsPerMonth: 2000,
    automations: 20,
    apiCallsPerMonth: 20000,
    storageMb: 10000,   // 10 GB
  },
  vip: {
    smsPerMonth: 2500,
    whatsappPerMonth: 2500,
    aiRequestsPerMonth: 10000,
    automations: 50,
    apiCallsPerMonth: 50000,
    storageMb: 20000,   // 20 GB
  },
  platinum: {
    smsPerMonth: 10000,
    whatsappPerMonth: 10000,
    aiRequestsPerMonth: 50000,
    automations: 200,
    apiCallsPerMonth: 200000,
    storageMb: 50000,   // 50 GB
  },
  enterprise: {
    smsPerMonth: "unlimited",
    whatsappPerMonth: "unlimited",
    aiRequestsPerMonth: "unlimited",
    automations: "unlimited",
    apiCallsPerMonth: "unlimited",
    storageMb: "unlimited",
  },
};

/**
 * Map backend plan keys to frontend BasePlanKey
 * Handles both uppercase (TRIAL, BASIC, etc.) and lowercase (trial, basic, etc.)
 */
export function normalizePlanKey(planKey: string | null | undefined): BasePlanKey {
  if (!planKey) return "trial";
  
  const normalized = planKey.toLowerCase().replace("_plus", "");
  
  const mapping: Record<string, BasePlanKey> = {
    "trial": "trial",
    "basic": "basic",
    "vip": "vip",
    "platinum": "platinum",
    "enterprise": "enterprise",
  };
  
  return mapping[normalized] || "trial";
}

/**
 * Check if a plan key indicates a Plus variant
 */
export function isPlusPlan(planKey: string | null | undefined): boolean {
  if (!planKey) return false;
  return planKey.toLowerCase().includes("_plus") || planKey.toLowerCase().includes("plus");
}

/**
 * PLAN_LABELS - Human-readable labels for all plans (including Plus variants)
 */
export const PLAN_LABELS: Record<PlanKey, string> = {
  trial: "Trial",
  trial_plus: "Trial Plus",
  basic: "Basic",
  basic_plus: "Basic Plus",
  vip: "VIP",
  vip_plus: "VIP Plus",
  platinum: "Platinum",
  platinum_plus: "Platinum Plus",
  enterprise: "Enterprise",
  enterprise_plus: "Enterprise Plus",
};

/**
 * Check if a numeric value exceeds a limit
 * Handles "unlimited", Infinity, and -1 values
 */
function exceedsLimit(value: number | "unlimited" | null | undefined, limit: number | "unlimited" | null | undefined): boolean {
  // If limit is unlimited, value never exceeds
  if (limit === "unlimited" || limit === Infinity || limit === -1 || limit === null || limit === undefined) {
    return false;
  }
  
  // If value is unlimited, it always exceeds a finite limit
  if (value === "unlimited" || value === Infinity || value === -1) {
    return true;
  }
  
  if (value === null || value === undefined) {
    return false;
  }
  
  // Both are numbers - compare
  return typeof value === 'number' && typeof limit === 'number' && !isNaN(value) && !isNaN(limit) && value > limit;
}

/**
 * Check if current company limits exceed the preset for the base plan
 * Returns true if any limit is greater than the preset (indicating customization/Plus)
 * 
 * @param currentLimits - Current company limits (from planUsage or company data)
 * @param basePlanKey - Base plan key (trial, basic, vip, etc.)
 * @returns boolean - true if limits are customized above preset
 */
export function isPlanCustomized(
  currentLimits: {
    smsPerMonth?: number | "unlimited" | null;
    whatsappPerMonth?: number | "unlimited" | null;
    aiRequestsPerMonth?: number | "unlimited" | null;
    automations?: number | "unlimited" | null;
    apiCallsPerMonth?: number | "unlimited" | null;
    storageMb?: number | "unlimited" | null; // storage in MB
  },
  basePlanKey: BasePlanKey
): boolean {
  const preset = PLAN_PRESETS[basePlanKey];
  if (!preset) return false;
  
  // Check each limit - if any exceeds preset, it's customized
  const smsExceeds = exceedsLimit(currentLimits.smsPerMonth, preset.smsPerMonth);
  const whatsappExceeds = exceedsLimit(currentLimits.whatsappPerMonth, preset.whatsappPerMonth);
  const aiExceeds = exceedsLimit(currentLimits.aiRequestsPerMonth, preset.aiRequestsPerMonth);
  const autoExceeds = exceedsLimit(currentLimits.automations, preset.automations);
  const apiExceeds = exceedsLimit(currentLimits.apiCallsPerMonth, preset.apiCallsPerMonth);
  const storageExceeds = exceedsLimit(currentLimits.storageMb, preset.storageMb);
  
  return smsExceeds || whatsappExceeds || aiExceeds || autoExceeds || apiExceeds || storageExceeds;
}

/**
 * Create display label for a plan (with Plus suffix if customized)
 * 
 * @param basePlanKey - Base plan key (trial, basic, vip, etc.)
 * @param isCustomized - Whether the plan is customized (Plus)
 * @returns string - Display label (e.g., "Basic", "Basic Plus")
 */
export function createDisplayPlanLabel(basePlanKey: BasePlanKey, isCustomized: boolean): string {
  const baseLabel = PLAN_LABELS[basePlanKey] || basePlanKey;
  return isCustomized ? `${baseLabel} Plus` : baseLabel;
}

/**
 * Get plan badge type from plan key (for CompanyFeatures component)
 * Maps plan keys to BadgeType
 */
export function getBadgeTypeFromPlan(planKey: string | null | undefined): "trial" | "basic" | "vip" | "platinum" | "enterprise" | null {
  if (!planKey) return null;
  
  const normalized = normalizePlanKey(planKey);
  const mapping: Record<BasePlanKey, "trial" | "basic" | "vip" | "platinum" | "enterprise"> = {
    trial: "trial",
    basic: "basic",
    vip: "vip",
    platinum: "platinum",
    enterprise: "enterprise",
  };
  
  return mapping[normalized] || null;
}

/**
 * Get plan key from badge type (for syncing between CompanyFeatures and CompanyDetails)
 */
export function getPlanKeyFromBadge(badgeType: "trial" | "basic" | "vip" | "platinum" | "enterprise" | null): BasePlanKey {
  if (!badgeType) return "trial";
  
  const mapping: Record<string, BasePlanKey> = {
    trial: "trial",
    basic: "basic",
    vip: "vip",
    platinum: "platinum",
    enterprise: "enterprise",
  };
  
  return mapping[badgeType] || "trial";
}

/**
 * Company data structure for plan limits calculation
 * Supports both SuperAdmin Company model and tenant-side company data
 */
export type CompanyPlanData = {
  plan?: string | null;
  limitsOverride?: {
    smsPerMonthOverride?: number | null;
    whatsappPerMonthOverride?: number | null; // NEW: WhatsApp override
    aiRequestsPerMonthOverride?: number | null;
    automationsOverride?: number | null;
    apiCallsPerMonthOverride?: number | null;
    storageLimitOverrideMb?: number | null;
    // Alternative field names (backend may use different names)
    maxSmsPerMonth?: number | null;
    maxWhatsAppPerMonth?: number | null; // NEW: WhatsApp alternative name
    maxAiRequestsPerMonth?: number | null;
    maxAutomations?: number | null;
    maxApiCallsPerMonth?: number | null;
    storageLimitOverride?: number | null;
  } | null;
  storageLimitOverride?: number | null; // Top-level override (in MB)
  storageLimitOverrideMb?: number | null; // Alternative name
};

/**
 * Get effective plan limits for a company
 * 
 * This function:
 * 1. Determines the base plan from company.plan
 * 2. Gets the preset limits from PLAN_PRESETS
 * 3. Applies any overrides from limitsOverride or top-level fields
 * 4. Returns normalized limits (number or 'unlimited')
 * 
 * @param company - Company object with plan and optional overrides
 * @returns PlanLimits - Effective limits for the company
 */
export function getEffectivePlanLimits(company: CompanyPlanData | null | undefined): PlanLimits {
  if (!company) {
    // Default to trial if no company data
    return PLAN_PRESETS.trial;
  }

  // 1. Determine base plan key
  const planKey = normalizePlanKey(company.plan);
  const preset = PLAN_PRESETS[planKey] || PLAN_PRESETS.trial;

  // 2. Get overrides (check both nested and top-level)
  const overrides = company.limitsOverride || {};
  
  // Helper to normalize a limit value
  const normalizeLimit = (
    overrideValue: number | null | undefined | "unlimited",
    presetValue: number | "unlimited"
  ): number | "unlimited" => {
    // If override is explicitly null or undefined, use preset
    if (overrideValue === null || overrideValue === undefined) {
      return presetValue;
    }
    
    // If override is "unlimited", return unlimited
    if (overrideValue === "unlimited" || overrideValue === Infinity || overrideValue === -1) {
      return "unlimited";
    }
    
    // If preset is unlimited and no override, return unlimited
    if (presetValue === "unlimited" && (overrideValue === null || overrideValue === undefined)) {
      return "unlimited";
    }
    
    // Override is a number, use it
    if (typeof overrideValue === "number" && Number.isFinite(overrideValue)) {
      return overrideValue;
    }
    
    // Fallback to preset
    return presetValue;
  };

  // 3. Apply overrides for each limit
  const smsOverride = 
    overrides.smsPerMonthOverride ?? 
    overrides.maxSmsPerMonth ?? 
    null;
  
  const whatsappOverride = 
    overrides.whatsappPerMonthOverride ?? 
    overrides.maxWhatsAppPerMonth ?? 
    null;
  
  const aiOverride = 
    overrides.aiRequestsPerMonthOverride ?? 
    overrides.maxAiRequestsPerMonth ?? 
    null;
  
  const automationsOverride = 
    overrides.automationsOverride ?? 
    overrides.maxAutomations ?? 
    null;
  
  const apiOverride = 
    overrides.apiCallsPerMonthOverride ?? 
    overrides.maxApiCallsPerMonth ?? 
    null;
  
  // For storage, handle bytes to MB conversion
  let storageOverride = 
    company.storageLimitOverrideMb ?? 
    overrides.storageLimitOverrideMb ?? 
    null;
  
  // If storageLimitOverride is in bytes (large number), convert to MB
  if (storageOverride === null && company.storageLimitOverride !== null && company.storageLimitOverride !== undefined) {
    const storageBytes = company.storageLimitOverride;
    // If it's a very large number (likely bytes), convert to MB
    // Otherwise assume it's already in MB
    if (storageBytes > 1024 * 1024) {
      storageOverride = storageBytes / (1024 * 1024);
    } else {
      // Assume it's in MB
      storageOverride = storageBytes;
    }
  }
  
  // If still null, check overrides.storageLimitOverride
  if (storageOverride === null && overrides.storageLimitOverride !== null && overrides.storageLimitOverride !== undefined) {
    const storageBytes = overrides.storageLimitOverride;
    if (storageBytes > 1024 * 1024) {
      storageOverride = storageBytes / (1024 * 1024);
    } else {
      // Assume it's in MB
      storageOverride = storageBytes;
    }
  }
  
  // CRITICAL: If preset is "unlimited" (Enterprise), always use unlimited regardless of override
  // Enterprise plan should never have storage limits
  if (preset.storageMb === "unlimited") {
    storageOverride = null; // Force use preset (unlimited)
  }
  
  // CRITICAL: If override exists but is less than preset, it's likely from old plan - use preset instead
  // This ensures that when plan is upgraded, old overrides don't override the new plan limits
  if (storageOverride !== null && typeof storageOverride === 'number' && typeof preset.storageMb === 'number') {
    if (storageOverride < preset.storageMb) {
      storageOverride = null; // Use preset instead - override is from old plan
    }
  }
  
  // Also check: if override is exactly equal to a lower plan's preset, it's likely from old plan
  // For example: VIP plan with 500 MB override (trial preset) should use 20 GB (VIP preset)
  if (storageOverride !== null && typeof storageOverride === 'number' && typeof preset.storageMb === 'number') {
    const knownPresets = [500, 10000, 20000, 50000]; // Trial, Basic, VIP, Platinum
    if (knownPresets.includes(storageOverride) && storageOverride < preset.storageMb) {
      storageOverride = null; // Use current plan preset
    }
  }

  // 4. Build effective limits
  const effectiveLimits = {
    smsPerMonth: normalizeLimit(smsOverride, preset.smsPerMonth),
    whatsappPerMonth: normalizeLimit(whatsappOverride, preset.whatsappPerMonth),
    aiRequestsPerMonth: normalizeLimit(aiOverride, preset.aiRequestsPerMonth),
    automations: normalizeLimit(automationsOverride, preset.automations),
    apiCallsPerMonth: normalizeLimit(apiOverride, preset.apiCallsPerMonth),
    storageMb: normalizeLimit(storageOverride, preset.storageMb),
  };
  
  return effectiveLimits;
}

/**
 * Format a limit value for display
 * 
 * @param limit - Limit value (number or 'unlimited')
 * @returns Object with isUnlimited flag and optional numeric value
 */
export function formatLimit(limit: number | "unlimited"): {
  isUnlimited: boolean;
  value?: number;
} {
  if (limit === "unlimited" || limit === Infinity || limit === -1) {
    return { isUnlimited: true };
  }
  
  if (typeof limit === "number" && Number.isFinite(limit)) {
    return { isUnlimited: false, value: limit };
  }
  
  // Default to unlimited if invalid
  return { isUnlimited: true };
}

/**
 * PLAN_ENTITY_LIMITS - User and client limits per plan
 * Defines maximum number of managers, agents, and clients allowed per plan
 */
export interface PlanEntityLimits {
  maxManagers: number | "unlimited";
  maxAgents: number | "unlimited";
  maxClients: number | "unlimited";
  maxContracts: number | "unlimited";
  maxLeads: number | "unlimited";
  maxPotentialClients: number | "unlimited";
}

export const PLAN_ENTITY_LIMITS: Record<BasePlanKey, PlanEntityLimits> = {
  trial: {
    maxManagers: 1,
    maxAgents: 1,
    maxClients: 20, // HARD LIMIT for Trial
    maxContracts: 1, // HARD LIMIT for Trial
    maxLeads: 10, // HARD LIMIT for Trial
    maxPotentialClients: 20, // HARD LIMIT for Trial
  },
  basic: {
    maxManagers: 2,
    maxAgents: 5,
    maxClients: 250,
    maxContracts: 50,
    maxLeads: 150,
    maxPotentialClients: 100, // HARD LIMIT for Basic plan
  },
  vip: {
    maxManagers: 30,
    maxAgents: 10,
    maxClients: "unlimited",
    maxContracts: "unlimited",
    maxLeads: "unlimited",
    maxPotentialClients: "unlimited",
  },
  platinum: {
    maxManagers: 100,
    maxAgents: 50,
    maxClients: "unlimited",
    maxContracts: "unlimited",
    maxLeads: "unlimited",
    maxPotentialClients: "unlimited",
  },
  enterprise: {
    maxManagers: "unlimited",
    maxAgents: "unlimited",
    maxClients: "unlimited",
    maxContracts: "unlimited",
    maxLeads: "unlimited",
    maxPotentialClients: "unlimited",
  },
} as const;

/**
 * Get entity limits (managers, agents, clients) for a company based on its plan
 * 
 * @param company - Company object with plan property
 * @returns PlanEntityLimits - Entity limits for the company's plan
 */
export function getPlanEntityLimits(company: CompanyPlanData | null | undefined): PlanEntityLimits {
  if (!company || !company.plan) {
    // Default to trial if no company or plan
    return PLAN_ENTITY_LIMITS.trial;
  }

  try {
    // Normalize plan key to get base plan
    const planKey = normalizePlanKey(company.plan);
    
    // TypeScript ensures planKey is BasePlanKey, which is a key of PLAN_ENTITY_LIMITS
    const limits = PLAN_ENTITY_LIMITS[planKey];
    
    if (limits) {
      return limits;
    }
    
    // Fallback to trial if limits is somehow undefined
    console.warn(`[getPlanEntityLimits] Entity limits not found for key: ${planKey}, defaulting to trial. Company plan was: ${company.plan}`);
    return PLAN_ENTITY_LIMITS.trial;
  } catch (error) {
    console.error(`[getPlanEntityLimits] Error getting entity limits for plan: ${company.plan}`, error);
    return PLAN_ENTITY_LIMITS.trial;
  }
}

